import { hideLoading, showLoading } from 'redux-store/reducers/loading-reducer';
import { RESET_ALL as RESET_ON_LOGOUT } from 'redux-store/reducers/common-reducer';

import { createReducer } from 'utils/redux';
import { API, get, patch, put } from 'utils/api';
import { Querystring } from 'utils/url';
import { snakeCaseKeys } from 'utils/keys';
import { logError } from 'utils/log';

import type { Action, CcmStatus, ThunkAction } from 'types';

const LOADING = 'admin/PROFILES_LOADING';
const PROFILES_LOADED = 'admin/PROFILES_LOADED';
const PROFILES_RESET = 'admin/PROFILES_RESET';
const CAMPAIGNS_WITH_UNRELEASED_PAYOUTS_LOADED = 'admin/CAMPAIGNS_WITH_UNRELEASED_PAYOUTS_LOADED';
const CAMPAIGNS_WITH_UNRELEASED_PAYOUTS_RESET = 'admin/CAMPAIGNS_WITH_UNRELEASED_PAYOUTS_RESET';
const PAGINATION_LIMIT = 50;

const { ADMIN_GET_BRANDS } = API.END_POINTS.API;
const { INFLUENCER_PROFILES } = API.END_POINTS.SEARCH;
const { ADMIN } = API.END_POINTS;

const initialState = {
  brandProfiles: [],
  influencerProfiles: [],
  campaignsWithUnreleasedPayouts: [],
  pagination: {},
  isLoading: false
};

function setLoadingStatus(status: boolean): Action {
  return {
    type: LOADING,
    payload: {
      status
    }
  };
}

function profilesLoaded(data): Action {
  return {
    type: PROFILES_LOADED,
    payload: {
      data
    }
  };
}

export function resetProfiles(): Action {
  return {
    type: PROFILES_RESET
  };
}

function campaignsWithUnreleasedPayoutsLoaded(data): Action {
  return {
    type: CAMPAIGNS_WITH_UNRELEASED_PAYOUTS_LOADED,
    payload: {
      data
    }
  };
}

export function resetCampaignsWithUnreleasedPayouts(): Action {
  return {
    type: CAMPAIGNS_WITH_UNRELEASED_PAYOUTS_RESET
  };
}

function capitalizeChannelUserName(responseData) {
  responseData.data.map(row => {
    const newRow = row;
    const channelNames = ['instagram', 'youtube'];
    channelNames.forEach(channelName => {
      if (row.channels && row.channels[channelName] && row.channels[channelName].username) {
        newRow.channels[channelName].userName = row.channels[channelName].username;
        delete newRow.channels[channelName].username;
      }
    });
    return newRow;
  });
  return responseData;
}

export function fetchProfiles(profileType = 'brand', query) {
  const approvalStatus = query.status === 'all' ? '' : (query.status || '');

  return (dispatch) => {
    const params = {
      page: query.page || 1,
      itemsPerPage: PAGINATION_LIMIT
    };

    let profileTypeApiEndpoint;
    const additionalParams: {
      name?: string,
      unverified?: boolean,
      search?: string,
      subscriptionType?: string,
      subscriptionStatus?: string,
      approvalStatus?: string,
      sort?: string,
      emailVerificationIsDone?: boolean,
      isDuplicate?: boolean
    } = {};

    if (profileType === 'influencer') {
      profileTypeApiEndpoint = INFLUENCER_PROFILES;
      additionalParams.name = query.search || '';
      additionalParams.unverified = false;
      additionalParams.emailVerificationIsDone = query.emailVerificationIsDone === 'all' || query.emailVerificationIsDone === undefined
        ? undefined : (query.emailVerificationIsDone === 'done');
      additionalParams.isDuplicate = query.isDuplicate === 'all' || query.isDuplicate === undefined ? undefined : (query.isDuplicate === 'yes');
    } else {
      profileTypeApiEndpoint = ADMIN_GET_BRANDS;
      additionalParams.search = query.search || '';
      additionalParams.subscriptionType = query.subscriptionType !== 'all' && query.subscriptionType || undefined;
      additionalParams.subscriptionStatus = query.subscriptionStatus !== 'all' && query.subscriptionStatus || undefined;
    }

    additionalParams.sort = 'joined.desc';
    if (approvalStatus) {
      additionalParams.approvalStatus = approvalStatus;
    }

    const queryString = Querystring.objectToString(snakeCaseKeys({ ...params, ...additionalParams }));
    const url = `${profileTypeApiEndpoint}?${queryString}`;

    dispatch(showLoading(url));

    return get(url)
      .then((response) => {
        const responseData = capitalizeChannelUserName(response.data);
        dispatch(profilesLoaded(responseData));
        dispatch(hideLoading(url));
      })
      .catch((error) => {
        dispatch(hideLoading(url));
        dispatch(setLoadingStatus(false));
        logError(error);
      });
  };
}

export const fetchCampaignsWithUnreleasedPayouts = (brandProfileId) => (
  async (dispatch) => { // eslint-disable-line consistent-return
    const url = ADMIN.getCampaignsWithUnreleasedPayouts(brandProfileId);

    dispatch(showLoading(url));

    try {
      const response = await get(url);
      const campaignsWithUnreleasedPayouts = response.data || [];

      dispatch(hideLoading(url));
      return dispatch(campaignsWithUnreleasedPayoutsLoaded(campaignsWithUnreleasedPayouts));
    } catch (error) {
      dispatch(hideLoading(url));
      dispatch(setLoadingStatus(false));
      logError(error);
    }
  }
);

export function forceProfileUpdate(profileId: number): ThunkAction {
  return async (dispatch) => {
    const url = ADMIN.crawlProfile(profileId);

    dispatch(showLoading(url));

    try {
      await get(url, {}, { suppressGlobalNotification: true });
    } catch (error) {
      logError(error);
    } finally {
      dispatch(hideLoading(url));
    }
  };
}

export const changeHiddenFromSearchStatus = (profileId: number, hidden: boolean) => (
  async (dispatch) => {
    const url = ADMIN.setHiddenFromSearchStatus(profileId);
    dispatch(showLoading(url));

    try {
      await put(url, {
        data: { hidden }
      });
    } catch (error) {
      logError(error);
    }
    return dispatch(hideLoading(url));
  });

export const updateCcmStatusForOrganisation = (id: number, ccmStatus: CcmStatus) => async dispatch => {
  const url = ADMIN.updateCcmStatus(id);

  try {
    dispatch(showLoading(url));
    await patch(url, { data: { ccmStatus } });

    return true;
  } catch (error) {
    logError(error);

    return false;
  } finally {
    dispatch(hideLoading(url));
  }
};

export default createReducer<any, any>('adminProfiles', initialState, {
  [RESET_ON_LOGOUT]: () => initialState,
  [LOADING]: (state, { status }) => ({
    ...state,
    isLoading: status
  }),
  [PROFILES_RESET]: (state) => ({
    ...state,
    brandProfiles: [],
    influencerProfiles: [],
    pagination: {}
  }),
  [PROFILES_LOADED]: (state, { data }) => ({
    ...state,
    brandProfiles: data.data,
    influencerProfiles: data.data,
    pagination: data.meta.pagination ? data.meta.pagination : {
      ...data.meta,
      links: data.links
    },
    isLoading: false
  }),
  [CAMPAIGNS_WITH_UNRELEASED_PAYOUTS_LOADED]: (state, { data }) => ({
    ...state,
    campaignsWithUnreleasedPayouts: data,
    isLoading: false
  }),
  [CAMPAIGNS_WITH_UNRELEASED_PAYOUTS_RESET]: (state) => ({
    ...state,
    campaignsWithUnreleasedPayouts: []
  })
});
